import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Backend } from "./contracts";
import type { CustomFieldDef, EntityType, Tag } from "./types";

function relayFrom(table: string) {
  return (supabase as unknown as SupabaseClient).schema("relay").from(table);
}

const now = () => new Date().toISOString();

/**
 * Live Backend over relay.custom_field_defs / custom_field_values / tags /
 * taggings. onConflict targets match the live unique constraints. This is the
 * single live implementation reused by both the record UIs and the importers
 * via makeContracts(makeSupabaseBackend(org)).
 */
export function makeSupabaseBackend(org: string): Backend {
  return {
    org,

    async findDef(entity_type: EntityType, field_key: string) {
      const { data, error } = await relayFrom("custom_field_defs")
        .select("*")
        .eq("organization_id", org)
        .eq("entity_type", entity_type)
        .eq("field_key", field_key)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as CustomFieldDef | null;
    },

    async upsertDef(def: CustomFieldDef) {
      const { error } = await relayFrom("custom_field_defs").upsert(def, {
        onConflict: "organization_id,entity_type,field_key",
      });
      if (error) throw error;
    },

    async getValue(field_id: string, entity_id: string) {
      const { data, error } = await relayFrom("custom_field_values")
        .select("value")
        .eq("field_id", field_id)
        .eq("entity_id", entity_id)
        .maybeSingle();
      if (error) throw error;
      return (data as { value: unknown } | null)?.value ?? null;
    },

    async setValue(field_id: string, entity_id: string, value: unknown) {
      // entity_type is NOT NULL on custom_field_values, but the Backend.setValue
      // signature doesn't carry it. Recover it from the owning field def.
      const { data: def, error: defErr } = await relayFrom("custom_field_defs")
        .select("entity_type")
        .eq("id", field_id)
        .maybeSingle();
      if (defErr) throw defErr;
      const entity_type = (def as { entity_type: string } | null)?.entity_type;
      if (!entity_type) throw new Error(`custom_field_def not found: ${field_id}`);

      const { error } = await relayFrom("custom_field_values").upsert(
        {
          organization_id: org,
          field_id,
          entity_id,
          entity_type,
          value,
          updated_at: now(),
        },
        { onConflict: "field_id,entity_id" }
      );
      if (error) throw error;
    },

    async findTag(slug: string, parent_id: string | null) {
      const base = relayFrom("tags")
        .select("*")
        .eq("organization_id", org)
        .eq("slug", slug);
      const { data, error } = parent_id
        ? await base.eq("parent_id", parent_id).maybeSingle()
        : await base.is("parent_id", null).maybeSingle();
      if (error) throw error;
      return (data ?? null) as Tag | null;
    },

    async listTags() {
      const { data, error } = await relayFrom("tags")
        .select("*")
        .eq("organization_id", org);
      if (error) throw error;
      return (data ?? []) as Tag[];
    },

    async upsertTag(tag: Tag) {
      const { error } = await relayFrom("tags").upsert(tag, { onConflict: "id" });
      if (error) throw error;
    },

    async hasTagging(tag_id: string, entity_type: EntityType, entity_id: string) {
      const { data, error } = await relayFrom("taggings")
        .select("id")
        .eq("tag_id", tag_id)
        .eq("entity_type", entity_type)
        .eq("entity_id", entity_id)
        .maybeSingle();
      if (error) throw error;
      return !!(data as { id: string } | null)?.id;
    },

    async addTagging(tag_id: string, entity_type: EntityType, entity_id: string) {
      const { error } = await relayFrom("taggings").upsert(
        { organization_id: org, tag_id, entity_type, entity_id },
        { onConflict: "tag_id,entity_type,entity_id" }
      );
      if (error) throw error;
    },

    async removeTagging(tag_id: string, entity_type: EntityType, entity_id: string) {
      const { error } = await relayFrom("taggings")
        .delete()
        .eq("tag_id", tag_id)
        .eq("entity_type", entity_type)
        .eq("entity_id", entity_id);
      if (error) throw error;
    },
  };
}
